import React, { useState, useEffect } from 'react';
import NodesTable from './components/NodesTable';
import ChatWidget from './components/ChatWidget';
import Login from './components/Login';
import { NodeDevice } from './types';
import { pb } from './services/pocketbase';

// Uso de encadenamiento opcional para prevenir el error "Cannot read properties of undefined"
const WEBHOOK_OBTENER = (import.meta as any).env?.VITE_WEBHOOK_OBTENER || "https://webhook.n8n.cr-safe.com/webhook/camaras-obtener";
const WEBHOOK_ACTUALIZAR = (import.meta as any).env?.VITE_WEBHOOK_ACTUALIZAR || "https://webhook.n8n.cr-safe.com/webhook/camaras-actualizar";

function App() {
    const [isAuthenticated, setIsAuthenticated] = useState(pb.authStore.isValid);
    const [nodes, setNodes] = useState<NodeDevice[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchNodes = async (isSilent = false) => {
        if (!isSilent) setLoading(true);
        try {
            const urlWithCacheBuster = `${WEBHOOK_OBTENER}?_t=${Date.now()}`;
            const response = await fetch(urlWithCacheBuster);
            
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            
            const data = await response.json();
            const items = Array.isArray(data) ? data : (data.data || []);
            const mapped = items.map((item: any, index: number) => ({
                id: item.id || item.ID || index + 1,
                name: item.Nombre || item.name || '',
                ip: item.IP || item.ip || '',
                channel: Number(item.CANAL || item.channel || 0),
                port: Number(item.PUERTO || item.port || 0),
                brand: item.MARCA || item.brand || '',
                type: item.TIPO || item.type || '',
                status: item.ESTADO || item.status || 'Null'
            }));
            setNodes(mapped);
        } catch (error) {
            console.error("Error al obtener nodos:", error);
        } finally {
            if (!isSilent) setLoading(false);
        }
    };

    const handleAction = async (type: 'CREATE' | 'UPDATE' | 'DELETE', data: any) => {
        if (type === 'CREATE') {
            const newCams = (Array.isArray(data) ? data : [data]).map((c: any, i: number) => ({ 
                ...c, 
                id: `temp-${Date.now()}-${i}`,
                status: c.status || 'Null'
            }));
            setNodes(prev => [...prev, ...newCams]);
        } else if (type === 'UPDATE') {
            setNodes(prev => prev.map(n => String(n.id) === String(data.id) || n.name === data.id ? { ...n, ...data.updates } : n));
        } else if (type === 'DELETE') {
            setNodes(prev => prev.filter(n => String(n.id) !== String(data) && n.name !== data));
        }

        try {
            const response = await fetch(WEBHOOK_ACTUALIZAR, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: type, data })
            });
            if (!response.ok) throw new Error('Error en la respuesta del servidor');
            setTimeout(() => fetchNodes(true), 2000);
        } catch (e) {
            console.warn("Sincronización fallida:", e);
        }
    };

    const handleLogout = () => {
        pb.authStore.clear();
        setIsAuthenticated(false);
    };

    useEffect(() => {
        if (isAuthenticated) fetchNodes();
    }, [isAuthenticated]);

    if (!isAuthenticated) {
        return <Login onLoginSuccess={() => setIsAuthenticated(true)} />;
    }

    const onlineCount = nodes.filter(n => n.status?.toUpperCase() === 'ONLINE').length;

    return (
        <div className="flex-col flex min-h-screen bg-[#0d1117] text-gray-200">
            <nav className="sticky top-0 z-40 w-full border-b border-[#30363d] bg-[#0d1117]/90 backdrop-blur-md">
                <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-6">
                        <div className="bg-primary px-4 py-2 rounded-lg shadow-lg shadow-primary/10">
                            <h1 className="text-xl font-black text-white axend-logo-text tracking-tighter italic leading-none">AXEND</h1>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <button onClick={() => fetchNodes()} disabled={loading} className="flex items-center gap-2 px-5 py-2.5 text-[10px] font-black text-white bg-primary hover:bg-white hover:text-primary rounded-xl transition-all shadow-lg shadow-primary/10 disabled:opacity-50">
                            <span className={`material-icons-round text-sm ${loading ? 'animate-spin' : ''}`}>sync</span>
                            {loading ? 'SINCRONIZANDO...' : 'REFRESCAR'}
                        </button>
                        <div className="h-6 w-px bg-[#30363d]"></div>
                        <button onClick={handleLogout} className="flex items-center gap-2 p-2.5 text-gray-500 hover:text-danger hover:bg-danger/5 rounded-xl transition-all">
                            <span className="material-icons-round text-lg">logout</span>
                        </button>
                    </div>
                </div>
            </nav>

            <main className="flex-grow max-w-[1600px] mx-auto w-full px-6 py-8">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
                    <div className="flex items-center gap-4">
                        <div className="bg-[#161b22] px-6 py-4 rounded-2xl border border-[#30363d] shadow-xl min-w-[160px]">
                            <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest mb-1 px-1">Cámaras</p>
                            <p className="text-3xl font-black text-white">{nodes.length}</p>
                        </div>
                        <div className="bg-[#161b22] px-6 py-4 rounded-2xl border border-[#30363d] shadow-xl min-w-[160px]">
                            <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest mb-1 px-1">En Línea</p>
                            <p className="text-3xl font-black text-success">{onlineCount}</p>
                        </div>
                    </div>
                    <div className="flex-grow max-w-xl">
                        <div className="relative group">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 material-icons-round text-gray-500 group-focus-within:text-primary transition-colors">search</span>
                            <input type="text" placeholder="Buscar por Nombre (MC) o IP..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full bg-[#161b22] border border-[#30363d] rounded-2xl py-4 pl-12 pr-4 text-sm font-medium text-white placeholder-gray-600 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all shadow-lg"/>
                        </div>
                    </div>
                </div>
                <div className="mb-6">
                    <h2 className="text-xs font-black text-gray-400 uppercase tracking-[0.3em] mb-6 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
                        Gestión de Infraestructura
                    </h2>
                    {loading && nodes.length === 0 ? (
                        <div className="w-full h-64 flex flex-col items-center justify-center bg-[#161b22] rounded-3xl border border-[#30363d] border-dashed">
                            <div className="w-10 h-10 border-2 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                            <p className="mt-4 text-[10px] font-black text-gray-500 tracking-[0.2em] uppercase">Mapeando red...</p>
                        </div>
                    ) : (
                        <NodesTable nodes={nodes} searchTerm={searchTerm} onDelete={(id) => handleAction('DELETE', id)} />
                    )}
                </div>
            </main>
            <ChatWidget onAction={handleAction} nodes={nodes} />
        </div>
    );
}

export default App;